import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { ApiUtils } from '@core/utils/api.utils';
import { AuthService, ConfigService } from '@services';
import { filter, Observable, switchMap, take, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { OAuth2Service } from '../auth/oAuth2.service';

@Injectable()
export class JWTInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  /**
   * Handles login by phone auth flow
   */
  private handleOAuth2TokenRefresh(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    oAuth2Service: OAuth2Service,
  ): Observable<HttpEvent<unknown>> {
    const TokenOAuth2 = oAuth2Service.getToken();

    return (oAuth2Service.isTokenRefreshProcessStart.value
      ? oAuth2Service.isTokenRefreshProcessStart
        .pipe(
          filter(value => !value),
          take(1),
        )
      : oAuth2Service.checkTokenValidityAndRefresh()
        .pipe(
          tap(() => oAuth2Service.isTokenRefreshProcessStart.next(false)),
          map(() => true),
        ))
      .pipe(
        switchMap(() => next.handle(
          req.clone({ setHeaders: { TokenOAuth2 } }),
        )),
      );
  }

  /**
   * Handles legacy login/password auth flow
   */
  private handleLegacyTokenAuth(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    authService: AuthService,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(
      req.clone({
        setHeaders: {
          token: authService.authToken,
          DeviceCode: authService.getDeviceCode(),
        },
      }),
    );
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const authService = this.injector.get(AuthService);
    const oAuth2Service = this.injector.get(OAuth2Service);
    const configService = this.injector.get(ConfigService);
    const isOauth2 = authService.isOauth2;
    const isAPI2Url = ApiUtils.isAPI2Url(req.url, configService.apiUrl);

    if (isAPI2Url) {
      if (isOauth2) {
        return this.handleOAuth2TokenRefresh(req, next, oAuth2Service);
      }

      if (authService.user && authService.authToken) {
        return this.handleLegacyTokenAuth(req, next, authService);
      }
    }

    return next.handle(req);
  }
}
